import { SERVER, DAYS_TO_NEXT_LOGIN_MS, LOGIN_PATH } from "appConfig";

import { i18n } from "services/i18nService";
import { removeItemInStorage } from "services/localStorageManager";
import { log } from "services/logService";
import { retrieveSettings } from "services/sosInventoryService/settings";
import { getSupportPrivileges } from "services/sosInventoryService/sosApi";

import {
  setNeedsCompanyChooser,
  setIsLoggedIn,
} from "globalState/authenticationSlice";
import { setBearerToken as setBearer } from "globalState/bearerTokenSlice";
import globalState from "globalState/globalState";
import {
  setUser,
  reset,
  setCompany,
  setCompanyList,
  setSupportPrivileges,
} from "globalState/userCompanySettingsSlice";

import { SOS_EMAIL_DOMAIN } from "appConstants";

var bearerToken = "";

export function getBearerToken() {
  return bearerToken?.length ? bearerToken : globalState.getState().bearerToken;
}

export function setBearerToken(token) {
  bearerToken = token;
  globalState.dispatch(setBearer(token));
}

export async function login(email, password, errorHandler, loadingHandler) {
  loadingHandler(true);
  const response = await fetch(SERVER + LOGIN_PATH, {
    method: "POST",
    body: JSON.stringify({ user: email, pass: password, source: "web" }),
  });
  const loginResponse = await response.json();
  const loginError = validateLoginResponse(loginResponse);
  if (loginError) {
    errorHandler(loginError);
    loadingHandler(false);
    return;
  }

  globalState.dispatch(setUser({ name: loginResponse.name, email }));

  const { selectedCompanyId, company, companies, needsCompanyChooser } =
    getCompanyData(loginResponse);

  globalState.dispatch(setNeedsCompanyChooser(needsCompanyChooser));
  globalState.dispatch(setCompany(company));
  globalState.dispatch(setCompanyList(companies));

  await updateTokens({
    email,
    password,
    loginResponse,
    selectedCompanyId,
    errorHandler,
    grantType: "password",
  });

  if (email.endsWith(SOS_EMAIL_DOMAIN)) {
    const { privileges } = await getSupportPrivileges();
    globalState.dispatch(setSupportPrivileges(privileges));
  }

  await retrieveSettings();

  setLastLoginCookie();

  globalState.dispatch(setIsLoggedIn("yes"));

  loadingHandler(false);
  return;
}

export function getCompanyData(loginResponse) {
  const { companies, defaultCompanyId } = loginResponse;
  const needsCompanyChooser = !defaultCompanyId && companies.length !== 1;
  const unsortedCompanies = companies.map((company) => {
    return {
      id: company.companyId,
      userId: company.userId,
      name: company.companyName,
      accessToken: company.accessToken,
      accessTokenSecret: company.accessTokenSecret,
      expirationDate: company.expirationDate,
      userPrivileges: company.permissionsList,
      planLevel: company.planLevel,
      status: company.status,
      accountNumber: company.acctNumber,
      default: company.companyId === defaultCompanyId,
      supportUser: company.supportUser,
    };
  });

  const company = defaultCompanyId
    ? unsortedCompanies.find(({ id }) => id === defaultCompanyId)
    : unsortedCompanies[0];
  const sortedCompanies = unsortedCompanies.sort((a, b) =>
    a.name < b.name ? -1 : 1
  );

  return {
    selectedCompanyId: company.id,
    company,
    companies: sortedCompanies,
    needsCompanyChooser,
  };
}

export async function updateTokens({
  email,
  password,
  loginResponse,
  selectedCompanyId,
  errorHandler,
  grantType,
}) {
  const tokenParams = getTokenParams(grantType, {
    email,
    password,
  });
  if (!tokenParams) {
    errorHandler(i18n("auth.Invalid"));
  }

  let tokens = {};
  const { companies } = loginResponse;
  const promises = companies.map(
    async ({ companyId, accessToken, accessTokenSecret }) => {
      const params = getTokenParams(grantType, {
        email,
        password,
        accessToken,
        accessTokenSecret,
        scope: companyId,
      });
      const body = Object.keys(params)
        .map((key) => key + "=" + encodeURIComponent(params[key]))
        .join("&");

      const response = await fetch(SERVER + "/oauth2/token", {
        method: "POST",
        body,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Host: SERVER,
        },
        credentials: "include",
      });
      const json = await response.json();
      if (response.ok) {
        const token = {
          email,
          token: json.access_token,
          refresh: json.refresh_token,
        };
        tokens[companyId] = token;
      } else {
        errorHandler(json.error_description);
      }
    }
  );

  await Promise.all(promises);
  setBearerToken(tokens[selectedCompanyId]?.token);
}

function validateLoginResponse(loginResponse) {
  if (loginResponse.status === "Error: Unknown error.") {
    return i18n("global.SomethingWentWrong");
  } else if (loginResponse.status === "invalid") {
    return i18n("auth.Invalid");
  } else if (loginResponse.status === "locked") {
    return i18n("auth.Locked");
  } else if (!loginResponse.companies.length) {
    return i18n("auth.NonUser");
  }
  return null;
}

export function getTokenParams(grantType, authData) {
  const { email, password, accessToken, accessTokenSecret, scope } = authData;
  switch (grantType) {
    case "password":
      return {
        scope,
        rememberMe: true,
        grant_type: grantType,
        username: email,
        password: password,
        client_id: "sos_web",
      };
    case "client_credentials":
      return {
        rememberMe: true,
        grant_type: grantType,
        client_id: accessToken,
        client_secret: accessTokenSecret,
      };
    default:
      return null;
  }
}

export function setLastLoginCookie() {
  const expires = new Date(Date.now() + DAYS_TO_NEXT_LOGIN_MS);
  document.cookie = `lastLogin=${expires}; expires=${expires.toUTCString()}; SameSite=Strict; path=/;`;
}

export function logout() {
  // call to server to clear the refresh token cookies; we don't care about
  // the result of this call; it sometimes returns a 500, but we've
  // determined that it's not an issue (https://app.clickup.com/t/2d4dj0n)
  try {
    fetch(SERVER + "/oauth2/token/logout", {
      method: "POST",
      credentials: "include",
      headers: new Headers({
        Authorization: "Bearer " + getBearerToken(),
      }),
    });
  } catch (error) {
    log.error(error);
  }
  setBearerToken("");
  document.cookie =
    "lastLogin=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;";
  globalState.dispatch(setIsLoggedIn("no"));
  globalState.dispatch(reset());
  removeItemInStorage("userCompanySettings");
}

export async function setAuthenticationState() {
  const currentCompany = globalState.getState().userCompanySettings.company.id;

  let isLoggedIn;
  if (document.cookie.indexOf("lastLogin=") === -1) {
    isLoggedIn = "no";
  } else if (getBearerToken()) {
    isLoggedIn = "yes";
  } else if (currentCompany) {
    await getRefreshedBearerToken(currentCompany);
    isLoggedIn = getBearerToken() ? "yes" : "no";
  } else {
    isLoggedIn = "no";
  }
  globalState.dispatch(setIsLoggedIn(isLoggedIn));
}

export async function getRefreshedBearerToken(company) {
  const params = {
    grant_type: "refresh_token",
    refresh_token: "ID:" + company,
    rememberMe: true,
  };

  const queryString = Object.keys(params)
    .map((key) => key + "=" + encodeURIComponent(params[key]))
    .join("&");
  const response = await fetch(SERVER + "/oauth2/token", {
    method: "POST",
    body: queryString,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Host: SERVER,
    },
    credentials: "include",
  });
  const json = await response.json();

  if (response.ok) {
    setBearerToken(json.access_token);
    return true;
  }
  return false;
}

export function getCompanyFromId(loginResponse, companyId) {
  const { companies, defaultCompanyId } = loginResponse;
  const needsCompanyChooser = !defaultCompanyId && companies.length !== 1;
  const unsortedCompanies = companies.map((company) => {
    return {
      id: company.companyId,
      userId: company.userId,
      name: company.companyName,
      accessToken: company.accessToken,
      accessTokenSecret: company.accessTokenSecret,
      expirationDate: company.expirationDate,
      userPrivileges: company.permissionsList,
      planLevel: company.planLevel,
      status: company.status,
      accountNumber: company.acctNumber,
      default: company.companyId === defaultCompanyId,
      supportUser: company.supportUser,
    };
  });

  const company = unsortedCompanies.find(({ id }) => id === companyId);
  const sortedCompanies = unsortedCompanies.sort((a, b) =>
    a.name < b.name ? -1 : 1
  );

  return {
    selectedCompanyId: company.id,
    company,
    companies: sortedCompanies,
    needsCompanyChooser,
  };
}
